import { useId } from 'react';
import type { CSSProperties } from 'react';

/**
 * Das Q in Bewegung -- der FORQ Login-Loader v1 als React-Baustein.
 *
 * Form, Ebenen und Klassennamen sind 1:1 das Fragment aus dem Pack
 * (docs/brand/login-loader/forq-loader-fragment.html); die Bewegung steckt in forq-loader.css.
 * Hier passiert nur, was das Pack dem Einbau ueberlaesst: eindeutige Kennungen je Instanz
 * (Maske, Clip, Verlaeufe), Groesse und Tempo als Eigenschaften, der Zustand von aussen.
 *
 * Das Pack taeuscht keinen Fortschritt vor: die Bewegung laeuft, bis die Anwendung `complete`
 * (oder `error`) setzt. Genau so wird es in der Startsequenz benutzt.
 */

export type ForqLoaderVariant = 'build' | 'sweep' | 'pulse';
export type ForqLoaderState = 'loading' | 'complete' | 'error';

interface ForqLoaderProps {
  /** `build` = Aufbau (Empfehlung des Packs), `sweep` = Lichtlauf, `pulse` = Impuls. */
  readonly variant?: ForqLoaderVariant;
  readonly state?: ForqLoaderState;
  /** Kantenlaenge der Zeichenflaeche in Pixel; das Pack sieht 24 bis 512 vor, Standard 156. */
  readonly size?: number;
  /** Sekunden je Durchlauf, Standard 2,8. */
  readonly duration?: number;
  /**
   * Statustext unter dem Q. Ohne Angabe beschriftet der Loader selbst ("FORQ wird geladen",
   * "Bereit.", "Laden fehlgeschlagen"); `null` laesst den Text weg, wenn der umgebende
   * Bildschirm ohnehin sagt, was passiert.
   */
  readonly label?: string | null;
  readonly className?: string;
}

// Die Q-Silhouette aus dem FORQ Asset Pack v1 -- unveraendert.
const shape = 'M479 109 C698 109 875 284 875 502 C875 590 848 674 799 737 C823 765 862 798 905 832 L865 889 L775 889 C761 889 750 888 742 882 L498 649 L592 554 L672 625 C696 590 711 547 711 502 C711 373 609 271 479 271 C349 271 259 373 259 502 C259 619 345 710 446 727 C475 732 489 730 510 746 L636 861 C587 881 534 891 479 891 C264 891 95 718 95 502 C95 284 264 109 479 109 Z';
// Der Ring ohne Auslauf: wird beim Aufbau durch die Maske "gezeichnet".
const ring = 'M479 109 C698 109 875 284 875 502 C875 590 848 674 799 737 L672 625 C696 590 711 547 711 502 C711 373 609 271 479 271 C349 271 259 373 259 502 C259 619 345 710 446 727 C475 732 489 730 510 746 L636 861 C587 881 534 891 479 891 C264 891 95 718 95 502 C95 284 264 109 479 109 Z';
// Der diagonale Auslauf: gleitet beim Aufbau an seine Position.
const tail = 'M799 737 C823 765 862 798 905 832 L865 889 L775 889 C761 889 750 888 742 882 L498 649 L592 554 L672 625 Z';
// Die Spur, entlang derer die Maske den Ring freigibt.
const ink = 'M572 817 C351 877 176 709 176 502 C176 326 311 190 479 190 C654 190 794 327 794 502 C794 586 764 656 718 699';

const defaultLabels: Record<ForqLoaderState, string> = {
  loading: 'FORQ wird geladen',
  complete: 'Bereit.',
  error: 'Laden fehlgeschlagen',
};

export function ForqLoader({
  variant = 'build',
  state = 'loading',
  size = 156,
  duration = 2.8,
  label,
  className,
}: ForqLoaderProps) {
  // Mehrere Loader auf einer Seite duerfen sich Maske, Clip und Verlaeufe nicht teilen (Pack-Hinweis).
  // useId liefert Zeichen, die in url(#...) stoeren koennten -- deshalb bereinigt.
  const prefix = `forq-loader-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const shapeId = `${prefix}-shape`;
  const clipId = `${prefix}-clip`;
  const revealId = `${prefix}-reveal`;
  const beamId = `${prefix}-beam`;
  const glintId = `${prefix}-glint`;

  const message = label === undefined ? defaultLabels[state] : label;
  const style = { '--fq-size': `${size}px`, '--fq-speed': `${duration}s` } as CSSProperties;

  return (
    <div className={className ? `forq-motion ${className}` : 'forq-motion'} data-variant={variant} data-state={state} style={style}>
      <div className="fq-art" aria-busy={state === 'loading'}>
        <svg className="fq-svg" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden focusable="false">
          <defs>
            <path id={shapeId} d={shape} />
            <clipPath id={clipId}><use href={`#${shapeId}`} /></clipPath>
            <mask id={revealId} maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000">
              <path className="fq-ink" d={ink} pathLength={100} stroke="white" strokeWidth={224} fill="none" strokeLinecap="butt" />
            </mask>
            <linearGradient id={beamId} x1="479" y1="80" x2="890" y2="502" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#ff2600" stopOpacity="0" />
              <stop offset=".42" stopColor="#ff2600" stopOpacity=".7" />
              <stop offset=".82" stopColor="#ff785b" />
              <stop offset="1" stopColor="#ffe2d7" />
            </linearGradient>
            <linearGradient id={glintId}>
              <stop offset="0" stopColor="white" stopOpacity="0" />
              <stop offset=".5" stopColor="white" stopOpacity=".48" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          <g className="fq-form">
            <use className="fq-ripple" href={`#${shapeId}`} />
            <use className="fq-base" href={`#${shapeId}`} />
            <path className="fq-ring" mask={`url(#${revealId})`} d={ring} />
            <path className="fq-tail" d={tail} />
            <use className="fq-finish" href={`#${shapeId}`} />
            <use className="fq-solid" href={`#${shapeId}`} />
            <g clipPath={`url(#${clipId})`}>
              <g className="fq-orbit"><path d="M479 502 L479 -200 A702 702 0 0 1 1181 502 Z" fill={`url(#${beamId})`} /></g>
              <g className="fq-shine"><path d="M-500 -100 L-230 -100 L750 1100 L480 1100 Z" fill={`url(#${glintId})`} /></g>
            </g>
          </g>
        </svg>
      </div>
      {message ? <p className="fq-message" role="status" aria-live="polite" aria-atomic="true">{message}</p> : null}
    </div>
  );
}
